/**
 * Ham metin kabul eden para/miktar alanı. `Decimal`'e dönüşümü YAPMAZ —
 * dönüşüm tek noktada (`lib/decimal.ts`) yapılır, bu bileşen onu çağırır ve
 * sonucunu kullanıcıya geri gösterir ("anlaşılan değer"), böylece belirsiz
 * girdilerde (örn. "1.234") yanlış anlaşılma görünür olur.
 */

import { useId, useState } from "react";
import type { Decimal } from "@parafon/contracts";
import { M } from "../dil";
import { DECIMAL_ERROR_TEXT, formatDecimal, parseDecimalInput } from "../lib/decimal";
import { Field, TextInput } from "./primitives";

export interface DecimalInputProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (raw: string) => void;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly hint?: string;
  readonly currency?: string;
  /** Dış doğrulamanın (form gönderimi) ürettiği hata. */
  readonly error?: string;
  readonly name?: string;
  readonly placeholder?: string;
}

export function DecimalInput({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  hint,
  currency,
  error,
  name,
  placeholder = formatDecimal("0", { fractionDigits: 2, gizlenmez: true }),
}: DecimalInputProps) {
  const id = useId();
  const [touched, setTouched] = useState(false);

  const parsed = value.trim() === "" ? null : parseDecimalInput(value);
  const liveError =
    parsed !== null && !parsed.ok && touched ? DECIMAL_ERROR_TEXT[parsed.error] : undefined;
  const shown = error ?? liveError;

  // Anlaşılan değeri geri yazmak, "1.234" gibi belirsiz girdilerin sessizce
  // yanlış kaydedilmesini engeller. İki türlü okunabilen yazımda (tek
  // ayırıcı + üç hane) geri yazılan metin girdiyle aynı olsa da gösterilir
  // (kullanıcı kararı 27.10.2026, seçenek c).
  const echo =
    parsed !== null &&
    parsed.ok &&
    (parsed.belirsiz === true || normalise(value) !== normalise(formatDecimal(parsed.value, { gizlenmez: true })))
      ? M.ui.anlasilan(`${formatDecimal(parsed.value, { gizlenmez: true })}${currency === undefined ? "" : ` ${currency}`}`)
      : undefined;

  return (
    <Field
      label={label}
      htmlFor={id}
      required={required}
      {...(shown === undefined ? {} : { error: shown })}
      {...(shown === undefined && (echo ?? hint) !== undefined ? { hint: echo ?? hint } : {})}
    >
      <div className="flex items-center gap-2">
        <TextInput
          id={id}
          numeric
          inputMode="decimal"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={shown !== undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
          onBlur={() => setTouched(true)}
          {...(name === undefined ? {} : { name })}
        />
        {currency !== undefined && (
          <span aria-hidden="true" className="w-10 shrink-0 text-[12px] text-faint">
            {currency}
          </span>
        )}
      </div>
    </Field>
  );
}

/** Girdiyi `Decimal`'e çevirir; boş ve geçersiz ayrı ayrı raporlanır. */
export function readDecimalField(
  raw: string,
  { required }: { required: boolean },
): { ok: true; value: Decimal | undefined } | { ok: false; message: string } {
  if (raw.trim() === "") {
    return required ? { ok: false, message: M.ui.alanZorunlu } : { ok: true, value: undefined };
  }
  const parsed = parseDecimalInput(raw);
  return parsed.ok ? { ok: true, value: parsed.value } : { ok: false, message: DECIMAL_ERROR_TEXT[parsed.error] };
}

function normalise(s: string): string {
  return s.replace(/\s/g, "");
}
